import React, { useEffect, useState } from 'react';
import { DOC_TYPES, docsFor } from '../shared/transactionDocs';
import { listDealDocuments, uploadDealDocument, deleteDealDocument } from '../services/dealDocuments';

// Transaction documents (executed contract, addenda, etc.) that the TC and agent can open from the deal.
export default function DealDocumentsSection({ ownerUid, transactionId, uploadedBy, transactionType, underContract }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Status checklist: the listing agreement, plus the under contract list once the deal is under contract.
  const isSeller = /seller/i.test(transactionType || '');
  const checklist = [
    ...(isSeller ? docsFor('Seller', 'listing') : []),
    ...((!isSeller || underContract) ? docsFor(transactionType, 'underContract') : []),
  ];
  const labelOf = (cat) => (DOC_TYPES.find((t) => t.key === cat) || {}).label;

  const reload = () => listDealDocuments(ownerUid, transactionId).then(setDocs).catch((err) => {
    console.error('Could not load documents', err);
    setError('Could not load documents.');
  });

  useEffect(() => { if (ownerUid && transactionId) reload(); }, [ownerUid, transactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      for (const file of files) {
        await uploadDealDocument(ownerUid, transactionId, file, uploadedBy);
      }
      await reload();
    } catch (err) {
      console.error('Upload failed', err);
      setError('Upload failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove ${doc.name}?`)) return;
    try {
      await deleteDealDocument(ownerUid, transactionId, doc);
      await reload();
    } catch (err) {
      console.error('Delete failed', err);
      setError('Could not remove that file.');
    }
  };

  return (
    <div style={{ border: '1px solid #e7dfcc', borderRadius: 8, padding: 12, margin: '16px 0', background: '#FFFFFF' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 15 }}>Documents</strong>
        <label style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', fontWeight: 700, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Uploading...' : 'Upload documents'}
          <input type="file" multiple onChange={onFiles} disabled={busy} style={{ display: 'none' }} />
        </label>
      </div>
      <p style={{ margin: '6px 0 8px', fontSize: 13, color: '#5B6270' }}>
        Executed contract, addenda, inspection and other documents for the TC. You can add more at any time.
      </p>
      {checklist.length > 0 && (
        <div style={{ margin: '0 0 10px' }}>
          {checklist.map((d) => {
            const n = docs.filter((x) => x.category === d.key).length;
            const status = n ? `Uploaded${n > 1 ? ` (${n})` : ''}` : d.required ? 'Missing' : 'Not added (optional)';
            const color = n ? '#2e7d32' : d.required ? '#D64545' : '#5B6270';
            return (
              <div key={d.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                <span>{d.label}</span><span style={{ color, fontWeight: 600 }}>{status}</span>
              </div>
            );
          })}
        </div>
      )}
      {error && <p style={{ color: '#D64545', fontSize: 13, margin: '0 0 8px' }}>{error}</p>}
      {docs.length === 0 && <p style={{ fontSize: 13, color: '#5B6270', margin: 0 }}>No documents uploaded yet.</p>}
      {docs.map((d) => (
        <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid #f0ead8', fontSize: 14 }}>
          <span><a href={d.url} target="_blank" rel="noreferrer" style={{ color: '#1A1A1A', fontWeight: 600 }}>{d.name}</a>
            {labelOf(d.category) && <span style={{ fontSize: 12, color: '#5B6270' }}> · {labelOf(d.category)}</span>}</span>
          <span style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: '#5B6270' }}>
            {d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString() : ''}
            <button type="button" onClick={() => remove(d)} style={{ background: 'none', border: 'none', color: '#D64545', cursor: 'pointer', fontSize: 12 }}>Remove</button>
          </span>
        </div>
      ))}
    </div>
  );
}
